import { Dialog, Theme } from '@infotech-soe/erp-ui-kit';
import { Stimulsoft } from '@infotech-soe/stimulsoft-reports-js/Scripts/stimulsoft.viewer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import PrintService from '../../../services/print/PrintService';
import { useAppSelector } from '../../../store/hooks';
import { getUserName } from '../../../store/selectors/session';
import useRuntimeConfig from '../../hooks/useRuntimeConfig';
import {
  getPrintReportInfoOption,
  setLicenseKey,
  StimulsoftViewer,
} from '../report-viewer';

import { TReviewConnectorProps } from './ReviewContainer';

const useStyles = createUseStyles({
  viewerWrapper: {
    height: '100%',
    overflow: 'hidden',
    borderBottomLeftRadius: 'var(--radiusBig)',
    borderBottomRightRadius: 'var(--radiusBig)',
  },
});

const ReviewDialogContent = ({
  data,
  template,
  variables,
  events,
  parseDates = true,
  isDarkMode,
  onClose,
}: TReviewConnectorProps): React.JSX.Element | null => {
  // eslint-disable-next-line no-console
  const handleError = useCallback((e: Error) => console.error(e), []);
  const runtimeConfig = useRuntimeConfig(handleError);
  const theme = useTheme<Theme>();
  const classes = useStyles();
  const printedBy = useAppSelector(getUserName);
  const [reportTemplate, setReportTemplate] = useState<string>();
  const [showPrintInfo, setShowPrintInfo] = useState(false);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    isLoadingRef.current = true;
    const load = async () => {
      const loadedTemplate = await new PrintService().getTemplate(
        template || ''
      );
      setReportTemplate(loadedTemplate);
    };
    void load();
    void getPrintReportInfoOption().then(setShowPrintInfo);
  }, [template]);

  const handleClose = useCallback(() => {
    if (!isLoadingRef.current) {
      onClose();
    }
  }, [onClose]);

  const ReportDescription = reportTemplate
    ? JSON.parse(reportTemplate)['ReportDescription']
    : undefined;

  if (!runtimeConfig?.stiLicenseKey) {
    return null;
  }

  setLicenseKey(runtimeConfig.stiLicenseKey);

  return (
    <Dialog
      height="95vh"
      noChildrenWrap
      open={true}
      title={ReportDescription}
      width="95vw"
      onClose={handleClose}
    >
      <div className={classes.viewerWrapper}>
        <StimulsoftViewer
          language="uk"
          reportData={data}
          reportTemplate={reportTemplate}
          variables={variables}
          events={events}
          parseDates={parseDates}
          printedBy={printedBy}
          showPrintInfo={showPrintInfo}
          isDarkMode={isDarkMode}
          onRendered={() => {
            isLoadingRef.current = false;
          }}
          appearance={{
            allowSelectionCurrentPage: false,
            backgroundColor: theme.colors.surface,
            scrollbarsMode: true,
            showPageShadow: true,
            showTooltipsHelp: false,
          }}
          exports={{ showExportToDocument: false }}
          toolbar={{
            borderColor: theme.colors.borderDivider,
            viewMode: Stimulsoft.Viewer.StiWebViewMode.Continuous,
          }}
          height="100%"
          tabIndex={0}
        />
      </div>
    </Dialog>
  );
};

const ReviewDialog = (
  props: TReviewConnectorProps
): React.JSX.Element | null =>
  props.isOpen ? <ReviewDialogContent key={props.template} {...props} /> : null;

export default ReviewDialog;
